import { Species } from '../data/species/types/Species';
import type { GuideDirectoryEntry } from '@/data/guideDirectory';
import {
  buildGuideDirectoryPath,
  formatGuideDirectoryLocation,
  resolveGuideDirectorySummary,
} from '@/data/guideDirectory';
import { SITE_COVER_PATH, SITE_COVER_WIDTH, SITE_COVER_HEIGHT } from '@/constants/brandAssets';
import { formatHostCategoryLabel } from '@/constants/hostComplianceUi';

/**
 * Public product + company brand (titles, og:site_name, JSON-LD `name`).
 * Same string as the company brand; legal entity stays distinct — see
 * `LEGAL_ENTITY_NAME`. Keep in sync with `api/_lib/seo-meta-resolver.js`.
 */
export const PRODUCT_DISPLAY_NAME = 'Wild Network'; // pragma: allowlist secret

/** Legal entity for Organization JSON-LD `legalName` and contracts. */
export const LEGAL_ENTITY_NAME = 'Wild Network LLC'; // pragma: allowlist secret

/** Production host for absolute URLs when `window` is unavailable (SSR, tests). */
export const CANONICAL_HOST = 'https://wildnetwork.app'; // pragma: allowlist secret

/** Base URL for canonical and OG URLs; use origin in browser so dev/staging work. */
const getBaseUrl = () =>
  typeof window !== 'undefined' ? window.location.origin : CANONICAL_HOST;

/** Default social share image when no page-specific image (1200×630). */
export const DEFAULT_OG_IMAGE_PATH = SITE_COVER_PATH;

/** Ensure image URL is absolute for OG/Twitter crawlers. */
const toAbsoluteImageUrl = (url: string | undefined): string | undefined => {
  if (!url) return undefined;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const base = getBaseUrl();
  return url.startsWith('/') ? `${base}${url}` : `${base}/${url}`;
};

const brandTitle = (pageTitle: string) => `${pageTitle} — ${PRODUCT_DISPLAY_NAME}`;
const brandTitleDash = (pageTitle: string) =>
  `${pageTitle} - ${PRODUCT_DISPLAY_NAME}`;

/**
 * Inject or replace a JSON-LD structured data block.
 * Call only in the browser (e.g. from `useEffect`).
 */
export const injectJsonLd = (data: Record<string, unknown>, pageKey: string): void => {
  if (typeof document === 'undefined') return;
  const attrKey = 'data-page-key';
  let script = document.querySelector(
    `script[type="application/ld+json"][${attrKey}="${pageKey}"]`,
  ) as HTMLScriptElement | null;
  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute(attrKey, pageKey);
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data);
};

/** Remove a previously injected JSON-LD block by pageKey (e.g. in `useEffect` cleanup). */
export const removeJsonLd = (pageKey: string): void => {
  if (typeof document === 'undefined') return;
  const script = document.querySelector(
    `script[type="application/ld+json"][data-page-key="${pageKey}"]`,
  );
  if (script) script.remove();
};

/** Landing (`/`) — keep in sync with api/_lib/seo-meta-resolver.js and public/index.html. */
export const LANDING_META_DESCRIPTION =
  'Live habitat conditions, a wild species field guide, and local outdoor guides — for foraging, hunting, fishing, and hiking across the U.S.';

export const generateLandingMetaTags = () => {
  const title = brandTitle('Maps, Field Conditions & Outdoor Guides');
  const description = LANDING_META_DESCRIPTION;

  return {
    title,
    description,
    ogTitle: title,
    ogDescription: description,
    ogImage: DEFAULT_OG_IMAGE_PATH,
    ogType: 'website',
    twitterCard: 'summary_large_image',
    twitterTitle: title,
    twitterDescription: description,
  };
};

/** Guide search (`/find`). */
export const generateGuideSearchMetaTags = (queryLabel: string) => {
  const title = queryLabel.trim()
    ? brandTitle(queryLabel.trim())
    : brandTitle('Search Guides');
  const description = `Find outdoor guides, outfitters, and local experts by activity and location on ${PRODUCT_DISPLAY_NAME}.`;

  return {
    title,
    description,
    ogTitle: title,
    ogDescription: description,
    ogImage: DEFAULT_OG_IMAGE_PATH,
    ogType: 'website',
    twitterCard: 'summary_large_image',
    twitterTitle: title,
    twitterDescription: description,
  };
};

/** Species detail (`/field-guide/:category/:slug`). */
export const generateSpeciesMetaTags = (species: Species) => {
  const title = brandTitleDash(
    `${species.name} - Identification, Season & Habitat`,
  );
  const seasonStart = species.season?.start ?? '';
  const seasonEnd = species.season?.end ?? '';
  const seasonRange = seasonStart && seasonEnd ? `${seasonStart}–${seasonEnd}` : '';
  const topStates = Array.isArray(species.states) ? species.states.slice(0, 3) : [];
  const topThreeStates =
    topStates.length > 0 ? topStates.join(', ') : 'the United States';

  const fallback = `Learn to identify ${species.name} (${species.scientificName}) — habitat, peak season${
    seasonRange ? ` (${seasonRange})` : ''
  }, and where to find it across ${topThreeStates}. Real-time foraging conditions updated daily.`;

  const body = species.description?.trim() ?? '';
  const description = body
    ? `${body.slice(0, 120).trimEnd()}. Peak season${
        seasonRange ? ` ${seasonRange}` : ''
      } across ${topThreeStates}.`
    : fallback;

  return {
    title,
    description,
    ogTitle: title,
    ogDescription: description,
    ogImage: species.imageUrl,
    ogType: 'article',
    twitterCard: 'summary_large_image',
    twitterTitle: title,
    twitterDescription: description,
    twitterImage: species.imageUrl,
  };
};

/**
 * Per-category meta copy for `/field-guide/:category`.
 * Keys are URL slugs from `keyToUrlSegmentMap` in `speciesCategoryUtils.ts`.
 * Keep in sync with `api/_lib/seo-meta-resolver.js`.
 */
export const FIELD_GUIDE_CATEGORY_META: Record<
  string,
  { title: string; description: string }
> = {
  mushrooms: {
    title: brandTitleDash('Wild Mushroom Field Guide'),
    description:
      'Identify edible wild mushrooms, discover peak fruiting windows, and find habitat maps for species across the United States. Real-time foraging conditions updated daily.',
  },
  fruits: {
    title: brandTitleDash('Wild Fruits & Berries Field Guide'),
    description:
      'Identify edible wild fruits and berries, discover peak harvest windows, and find habitat maps for species across the United States. Real-time foraging conditions updated daily.',
  },
  greens: {
    title: brandTitleDash('Wild Greens Field Guide'),
    description:
      'Identify edible wild greens, discover peak harvest windows, and find habitat maps for species across the United States. Real-time foraging conditions updated daily.',
  },
  nuts: {
    title: brandTitleDash('Wild Nuts & Seeds Field Guide'),
    description:
      'Identify edible wild nuts and seeds, discover peak harvest windows, and find habitat maps for species across the United States. Real-time foraging conditions updated daily.',
  },
  roots: {
    title: brandTitleDash('Wild Roots Field Guide'),
    description:
      'Identify edible wild roots and tubers, discover peak harvest windows, and find habitat maps for species across the United States. Real-time foraging conditions updated daily.',
  },
  other: {
    title: brandTitleDash('Wild Species Field Guide'),
    description:
      'Identify edible wild species, discover peak harvest windows, and find habitat maps across the United States. Real-time foraging conditions updated daily.',
  },
  wildflowers: {
    title: brandTitleDash('Wildflower Field Guide'),
    description:
      'Discover peak bloom windows and habitats for wildflowers across the United States. Plan photography trips and nature outings with seasonal field intelligence.',
  },
  fish: {
    title: brandTitleDash('Fish Species Field Guide'),
    description: `Learn about popular game fish species, seasonal runs, and habitats across the United States. Plan angling trips with field intelligence from ${PRODUCT_DISPLAY_NAME}.`,
  },
  game: {
    title: brandTitleDash('Game Species Field Guide'),
    description:
      'Explore upland game and big-game species, seasons, and habitats across the United States. Plan hunting and wildlife trips with field intelligence.',
  },
};

export const FIELD_GUIDE_INDEX_META = {
  title: brandTitleDash('Wild Foods Field Guide'),
  description:
    'Identify wild mushrooms, edible plants, and seasonal forage across the United States. Real-time foraging conditions updated daily.',
};

/**
 * Field guide list / category pages (`/field-guide`, `/field-guide/:category`).
 * Pass the URL segment (e.g. `mushrooms`, `greens`) to get per-category copy.
 */
export const generateSpeciesListMetaTags = (categoryUrlSegment?: string) => {
  const { title, description } =
    (categoryUrlSegment && FIELD_GUIDE_CATEGORY_META[categoryUrlSegment]) ||
    FIELD_GUIDE_INDEX_META;

  return {
    title,
    description,
    ogTitle: title,
    ogDescription: description,
    ogImage: DEFAULT_OG_IMAGE_PATH,
    ogType: 'website',
    twitterCard: 'summary_large_image',
    twitterTitle: title,
    twitterDescription: description,
  };
};

/** Category browse (`/events/categories/:slug`). */
export const generateEventCategoryMetaTags = (category: {
  label: string;
  description?: string;
}) => {
  const title = brandTitleDash(`${category.label} Adventures & Guided Experiences`);
  const description =
    category.description?.trim() ||
    `Browse ${category.label.toLowerCase()} adventures led by local hosts on ${PRODUCT_DISPLAY_NAME}.`;

  return {
    title,
    description,
    ogTitle: title,
    ogDescription: description,
    ogImage: DEFAULT_OG_IMAGE_PATH,
    ogType: 'website',
    twitterCard: 'summary_large_image',
    twitterTitle: title,
    twitterDescription: description,
  };
};

/** Events catalog (`/events`) — gated for now; kept for when the full catalog ships. */
export const generateEventsMetaTags = () => {
  const title = brandTitleDash('Outdoor Adventures & Guided Experiences');
  const description = `Browse host-led foraging, hiking, fishing, climbing, and field experiences by category. Book adventures across the United States on ${PRODUCT_DISPLAY_NAME}.`;

  return {
    title,
    description,
    ogTitle: title,
    ogDescription: description,
    ogImage: DEFAULT_OG_IMAGE_PATH,
    ogType: 'website',
    twitterCard: 'summary_large_image',
    twitterTitle: title,
    twitterDescription: description,
  };
};

/**
 * Shared fall-color link (`/fall-color` → `/maps/fall-color`). No region in the
 * copy: the link opens wherever the viewer is, and coverage is set to widen.
 * Keep in sync with `api/_lib/seo-meta-resolver.js` FALL_COLOR_META.
 */
export const FALL_COLOR_META_TITLE = brandTitle('Fall Color Map — Where the Leaves Are Turning');
export const FALL_COLOR_META_DESCRIPTION =
  'See where fall color is turning, peaking, and past right now — read from recent satellite passes and advanced by local weather. Opens wherever you are.';

export const generateFallColorMetaTags = () => ({
  title: FALL_COLOR_META_TITLE,
  description: FALL_COLOR_META_DESCRIPTION,
  ogTitle: FALL_COLOR_META_TITLE,
  ogDescription: FALL_COLOR_META_DESCRIPTION,
  ogImage: DEFAULT_OG_IMAGE_PATH,
  ogType: 'website',
  twitterCard: 'summary_large_image',
  twitterTitle: FALL_COLOR_META_TITLE,
  twitterDescription: FALL_COLOR_META_DESCRIPTION,
  canonicalUrl: `${CANONICAL_HOST}/fall-color`,
});

/**
 * `/fall` landing page (`/autumn` 301s here). Keep in sync with
 * `api/_lib/seo-meta-resolver.js` FALL_LANDING_META.
 */
export const FALL_LANDING_META_TITLE = brandTitle(
  'Fall Color Map & Guide — Which Trees Turn and When',
);
export const FALL_LANDING_META_DESCRIPTION =
  'A live fall foliage map read from satellite passes, plus a guide to the trees that turn — aspen, maple, oak, cottonwood, larch — their colors, and when each one peaks.';

export const generateFallLandingMetaTags = () => ({
  title: FALL_LANDING_META_TITLE,
  description: FALL_LANDING_META_DESCRIPTION,
  ogTitle: FALL_LANDING_META_TITLE,
  ogDescription: FALL_LANDING_META_DESCRIPTION,
  ogImage: DEFAULT_OG_IMAGE_PATH,
  ogType: 'website',
  twitterCard: 'summary_large_image',
  twitterTitle: FALL_LANDING_META_TITLE,
  twitterDescription: FALL_LANDING_META_DESCRIPTION,
  canonicalUrl: `${CANONICAL_HOST}/fall`,
  robots: 'index' as const,
});

/** Public teaser while the events catalog is behind a feature flag. */
export const generateEventsComingSoonMetaTags = () => {
  const title = brandTitleDash('Guided Foraging & Nature Experiences');
  const description = `Host-led foraging tours, field workshops, and wild food experiences are coming to ${PRODUCT_DISPLAY_NAME}. Explore maps and the field guide while we finish the calendar.`;

  return {
    title,
    description,
    ogTitle: title,
    ogDescription: description,
    ogImage: DEFAULT_OG_IMAGE_PATH,
    ogType: 'website',
    twitterCard: 'summary_large_image',
    twitterTitle: title,
    twitterDescription: description,
  };
};



/** Guide directory detail (`/guides/:slug`). */
export const generateGuideDirectoryMetaTags = (
  entry: GuideDirectoryEntry,
  options?: { photoUrl?: string },
) => {
  const location = formatGuideDirectoryLocation(entry);
  const activity = formatHostCategoryLabel(entry.category);
  const title = brandTitle(`${entry.name} — ${activity} in ${location}`);
  const summary = resolveGuideDirectorySummary(entry);
  const about = entry.about?.trim();
  const rawDescription =
    summary ||
    about ||
    `${entry.name} offers ${activity.toLowerCase()} experiences in ${location} on ${PRODUCT_DISPLAY_NAME}.`;
  const description =
    rawDescription.length > 155
      ? `${rawDescription.slice(0, 152).trimEnd()}…`
      : rawDescription;
  const path = buildGuideDirectoryPath(entry);
  const canonicalUrl = `${CANONICAL_HOST}${path}`;
  const ogImage = options?.photoUrl || entry.photoUrl || DEFAULT_OG_IMAGE_PATH;

  return {
    title,
    description,
    ogTitle: title,
    ogDescription: description,
    ogImage,
    ogType: 'website',
    twitterCard: 'summary_large_image',
    twitterTitle: title,
    twitterDescription: description,
    twitterImage: ogImage,
    canonicalUrl,
  };
};

/** Legal / changelog / support public pages. */
export const generateLegalPageMetaTags = (args: {
  title: string;
  description: string;
  path: string;
}) => {
  const title = brandTitle(args.title);
  const canonicalUrl = `${CANONICAL_HOST}${args.path}`;

  return {
    title,
    description: args.description,
    ogTitle: title,
    ogDescription: args.description,
    ogImage: DEFAULT_OG_IMAGE_PATH,
    ogType: 'website',
    twitterCard: 'summary_large_image',
    twitterTitle: title,
    twitterDescription: args.description,
    canonicalUrl,
  };
};

type DocumentHeadMeta = {
  title: string;
  description: string;
  ogTitle: string;
  ogDescription: string;
  ogImage?: string;
  ogType: string;
  twitterCard: string;
  twitterTitle: string;
  twitterDescription: string;
  twitterImage?: string;
  /** Full canonical URL; defaults to current origin + pathname. */
  canonicalUrl?: string;
  /** Optional robots directive (e.g. noindex for app-shell routes). */
  robots?: 'index' | 'noindex' | 'noindex, nofollow';
};

/**
 * Update document head with meta tags.
 * Uses absolute URLs for og:image, twitter:image, og:url, and link rel=canonical.
 * Keep title/description generators in sync with `api/_lib/seo-meta-resolver.js`.
 */
export const updateDocumentHead = (metaTags: DocumentHeadMeta) => {
  const baseUrl = getBaseUrl();
  const ogImage = toAbsoluteImageUrl(metaTags.ogImage);
  const twitterImage = toAbsoluteImageUrl(metaTags.twitterImage ?? metaTags.ogImage);
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const ogUrl = metaTags.canonicalUrl ?? `${baseUrl}${pathname}`;

  document.title = metaTags.title;

  const updateMetaTag = (name: string, content: string, property?: boolean) => {
    const attribute = property ? 'property' : 'name';
    let meta = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute(attribute, name);
      document.head.appendChild(meta);
    }
    meta.content = content;
  };

  updateMetaTag('description', metaTags.description);

  updateMetaTag('og:title', metaTags.ogTitle, true);
  updateMetaTag('og:description', metaTags.ogDescription, true);
  updateMetaTag('og:type', metaTags.ogType, true);
  updateMetaTag('og:url', ogUrl, true);
  updateMetaTag('og:site_name', PRODUCT_DISPLAY_NAME, true);
  if (ogImage) {
    updateMetaTag('og:image', ogImage, true);
    updateMetaTag('og:image:secure_url', ogImage, true);
    const isDefaultCover =
      metaTags.ogImage === DEFAULT_OG_IMAGE_PATH ||
      metaTags.ogImage === SITE_COVER_PATH ||
      ogImage.endsWith(SITE_COVER_PATH);
    if (isDefaultCover) {
      updateMetaTag('og:image:type', 'image/jpeg', true);
      updateMetaTag('og:image:width', String(SITE_COVER_WIDTH), true);
      updateMetaTag('og:image:height', String(SITE_COVER_HEIGHT), true);
    }
  }

  updateMetaTag('twitter:card', metaTags.twitterCard);
  updateMetaTag('twitter:title', metaTags.twitterTitle);
  updateMetaTag('twitter:description', metaTags.twitterDescription);
  if (twitterImage) {
    updateMetaTag('twitter:image', twitterImage);
  }

  if (metaTags.robots) {
    updateMetaTag('robots', metaTags.robots);
  }

  let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }
  canonical.href = ogUrl;
};
